import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Divider, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { useFieldArray, useFormContext } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";

import Intervals from "./Intervals";
import { DateRule, FormValues } from "../SchedulerPage";

type Props = {
  visible: boolean;
  onClose: () => void;
  onRemove: (overrideRuleIndex: number) => void;
  handleDateUpdate: (date: Date | null) => void;
  ruleIndex: number;
};

const StyledButton = withStyles(Button, {
  root: {
    height: 32,
    minWidth: 124,
  },
});

const StyledDialog = withStyles(Dialog, {
  paper: {
    maxWidth: 800,
  },
});

const useStyles = makeStyles()(() => ({
  root: {
    width: "100%",
    marginTop: 8,
    marginBottom: 14,
  },
  icon: {
    color: "#7D8797",
    height: 20,
    width: 20,
  },
}));

const OverrideDateDialog: React.FC<Props> = ({
  visible,
  onClose,
  handleDateUpdate,
  ruleIndex,
  onRemove,
}) => {
  const formMethods = useFormContext<FormValues>();
  const {
    watch,
    control,
    getValues,
    formState: { isDirty },
  } = formMethods;
  const { append, remove, fields } = useFieldArray({
    control,
    name: `dateRules.${ruleIndex}.intervals`,
  });

  const currentInterval = getValues(`dateRules.${ruleIndex}.intervals`);

  const [displayInterval, setDisplayInterval] = useState<boolean>(false);

  function handleAdd() {
    onClose();
  }

  function renderIntervals() {
    if (currentInterval?.length > 0) {
      return currentInterval
        .filter((e) => !e._destroy)
        .map((e, intervalIndex: number) => (
          <Intervals
            key={intervalIndex}
            ruleIndex={ruleIndex}
            intervalIndex={intervalIndex}
            onRemove={() => remove(intervalIndex)}
            override={true}
          />
        ));
    } else {
      return <Typography variant="h6">No Schedule set</Typography>;
    }
  }

  const { classes } = useStyles();

  return (
    <StyledDialog
      open={visible}
      onClose={() => {
        onClose();
        setDisplayInterval(false);
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        margin={4}
      >
        <DialogTitle fontWeight="bold">Select dates to override</DialogTitle>
        <IconButton
          onClick={() => {
            onClose();
            setDisplayInterval(false);
            onRemove(ruleIndex);
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box display="flex" paddingRight={4} paddingLeft={4} paddingBottom={4}>
        <div>
          <StaticDatePicker
            onAccept={(date: Date | null) => {
              handleDateUpdate(date);
              setDisplayInterval(true);
            }}
            disablePast
            displayStaticWrapperAs="desktop"
          />
          <Divider light />
          <Box
            marginTop={4}
            display="flex"
            justifyContent="space-between"
            paddingLeft={4}
            paddingRight={4}
          >
            <StyledButton
              variant="outlined"
              onClick={() => {
                onClose();
                onRemove(ruleIndex);
              }}
            >
              Cancel
            </StyledButton>
            <StyledButton
              disabled={!formMethods.formState.isValid || !isDirty}
              type="submit"
              onClick={handleAdd}
            >
              Apply
            </StyledButton>
          </Box>
        </div>
        {displayInterval && (
          <Box marginLeft={8} display="column">
            <Box display="flex" justifyContent="space-between" marginBottom={8}>
              <Typography variant="h6" fontWeight="bold">
                What hours are you available?
              </Typography>
              <IconButton
                sx={{ marginLeft: 8 }}
                onClick={() => {
                  append({ startTime: null, endTime: null, _destroy: false });
                }}
              >
                <AddIcon className={classes.icon} />
              </IconButton>
            </Box>
            {renderIntervals()}
          </Box>
        )}
      </Box>
    </StyledDialog>
  );
};

export default OverrideDateDialog;
